import React from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { formatDate, getMediaTypeFromUrl } from '../../../utils/helpers';

const ArticlePreview = ({ 
  imageUrl, 
  title,
  caption,
  description,
  richDescription,
  articleDetails = {},
  specifications = {}
}) => {
  const { isRTL } = useLanguage();
  const aspectRatio = specifications?.aspect_ratio || '2:1';
  
  // Get aspect ratio class for featured image
  const getAspectClass = () => {
    switch (aspectRatio) {
      case '2:1': return 'aspect-[2/1]';
      case '16:9': return 'aspect-video';
      case '4:3': return 'aspect-[4/3]';
      default: return 'aspect-[2/1]';
    }
  };

  const author = articleDetails?.author;
  const category = articleDetails?.category;
  const tags = articleDetails?.tags || [];
  const publishDate = new Date().toISOString(); // In real app, use actual publish date

  return (
    <div className="w-full max-w-[700px] mx-auto">
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-lg">
        {/* Featured Image/Video */}
        <div className={`relative bg-gray-100 ${getAspectClass()}`}>
          {imageUrl ? (
            getMediaTypeFromUrl(imageUrl) === 'video' ? (
              <video
                src={imageUrl}
                className="w-full h-full object-cover"
                controls
                muted
                playsInline
              />
            ) : (
              <img 
                src={imageUrl} 
                alt={title || 'Article'} 
                className="w-full h-full object-cover"
              />
            )
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
              <div className="text-center p-4">
                <span className="text-5xl block mb-2">📝</span>
                <p className="text-gray-500 text-sm">
                  {isRTL ? 'صورة المقال المميزة' : 'Article Featured Image'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Article Content */}
        <div className="p-6">
          {/* Category */}
          {category && (
            <div className="mb-3">
              <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium">
                {category}
              </span>
            </div>
          )}

          {/* Title */}
          {title && (
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 leading-tight">
              {title}
            </h1>
          )}

          {/* Meta Information */}
          <div className="flex items-center gap-4 text-sm text-gray-600 mb-4 flex-wrap">
            {author && (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
                  <span className="text-xs font-medium">
                    {author.charAt(0).toUpperCase()}
                  </span>
                </div>
                <span>{author}</span>
              </div>
            )}
            <span>•</span>
            <span>{formatDate(publishDate)}</span>
            <span>•</span>
            <span>{isRTL ? '5 دقائق قراءة' : '5 min read'}</span>
          </div>

          {/* Excerpt/Description */}
          {richDescription ? (
            <div 
              className="text-gray-700 mb-4 leading-relaxed [&>p]:mb-3 [&>h1]:text-2xl [&>h1]:font-bold [&>h1]:mb-2 [&>h2]:text-xl [&>h2]:font-bold [&>h2]:mb-2 [&>h3]:text-lg [&>h3]:font-semibold [&>h3]:mb-2 [&>ul]:list-disc [&>ul]:ml-6 [&>ol]:list-decimal [&>ol]:ml-6 [&>li]:mb-1 [&>a]:text-primary-600 [&>a]:hover:text-primary-700 [&>a]:underline [&>strong]:font-bold [&>em]:italic"
              dangerouslySetInnerHTML={{ __html: richDescription }}
            />
          ) : (description || caption) ? (
            <p className="text-gray-700 mb-4 leading-relaxed line-clamp-3">
              {description || caption}
            </p>
          ) : null}

          {/* Tags */}
          {tags.length > 0 && (
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {tags.slice(0, 5).map((tag, index) => (
                <span 
                  key={index}
                  className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded"
                >
                  #{tag}
                </span>
              ))}
              {tags.length > 5 && (
                <span className="text-xs text-gray-400">+{tags.length - 5} more</span>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <button className="text-primary-600 hover:text-primary-700 font-medium">
              {isRTL ? 'اقرأ المزيد' : 'Read More →'}
            </button>
            <div className="flex items-center gap-4">
              <button className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
              </button>
              <button className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
      <p className="text-center text-xs text-gray-500 mt-2">
        {isRTL ? 'معاينة المقال' : 'Blog Article Preview'}
      </p>
    </div>
  );
};

export default ArticlePreview;

